import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, AlertCircle, LogIn } from 'lucide-react';
import { api, parseApiError } from '../lib/api';
import { getStoredToken } from '../lib/auth';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';

// Open shareable seat-claim link (`/claim-seat/:token`). Unlike /claim/:token
// (a Discord-bound handshake that signs you in AS the target), this uses the
// OPENER's own login: sign in, then the 24h token claims the reserved seat for
// whoever you are.
export function ClaimSeatPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<
    | { kind: 'claiming' }
    | { kind: 'need-login' }
    | { kind: 'error'; message: string }
    | { kind: 'done'; clanName: string; gamertag: string }
  >({ kind: 'claiming' });

  useEffect(() => {
    if (!token) { setState({ kind: 'error', message: 'No claim link in the URL.' }); return; }
    if (!getStoredToken()) { setState({ kind: 'need-login' }); return; }
    let cancelled = false;
    api.claimSeatViaLink(token)
      .then(res => {
        if (cancelled) return;
        qc.invalidateQueries({ queryKey: ['me'] });
        setState({ kind: 'done', clanName: res.clanName, gamertag: res.gamertag });
      })
      .catch(err => {
        if (cancelled) return;
        setState({ kind: 'error', message: parseApiError(err).message ?? 'Could not claim the seat.' });
      });
    return () => { cancelled = true; };
  }, [token, qc]);

  if (state.kind === 'claiming') return <PageSpinner />;

  if (state.kind === 'need-login') {
    const signIn = () => {
      window.location.href = `/auth/discord/start?returnTo=${encodeURIComponent(`/claim-seat/${token}`)}`;
    };
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Card>
          <h1 className="font-display text-xl font-black uppercase tracking-wider text-white">Claim your seat</h1>
          <p className="mt-3 text-sm text-iron-300">
            Sign in with Discord to claim the seat your team leader reserved for you — you'll come right back here.
          </p>
          <button
            onClick={signIn}
            className="ember mt-6 inline-flex items-center gap-2 border border-forge-500/60 bg-forge-600/30 px-4 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-forge-500/40"
          >
            <LogIn className="h-4 w-4" strokeWidth={2.5} /> Sign in with Discord
          </button>
        </Card>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Card className="border-red-700/40">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 shrink-0 text-red-400" />
            <div>
              <h1 className="font-display text-lg font-black uppercase tracking-wider text-red-200">Couldn't claim the seat</h1>
              <p className="mt-2 text-sm text-iron-200">{state.message}</p>
              <p className="mt-3 text-xs text-iron-500">Ask your team leader to send you a fresh link.</p>
              <Link to="/" className="mt-4 inline-block font-display text-[11px] font-black uppercase tracking-wider text-forge-300 hover:text-forge-200">
                ← Back to portal
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-12">
      <Card>
        <h1 className="font-display text-xl font-black uppercase tracking-wider text-emerald-300">
          You're in — welcome to {state.clanName}
        </h1>
        <p className="mt-3 text-sm text-iron-300">
          The <code className="font-mono text-amber-200">{state.gamertag}</code> seat is yours.
        </p>
        <button
          onClick={() => { qc.invalidateQueries({ queryKey: ['me'] }); navigate('/me/org', { replace: true }); }}
          className="ember mt-6 inline-flex items-center gap-2 border border-forge-500/60 bg-forge-600/30 px-4 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] text-white transition-colors hover:bg-forge-500/40"
        >
          <Check className="h-4 w-4" strokeWidth={2.5} /> Take me in
        </button>
      </Card>
    </div>
  );
}
